import SVM from "libsvm-js/asm";
import { ALL_MODELS } from "../constants";
import { convertModelRF } from "../MachineLearning/BackgroundTrainer";
import { LogisticRegression } from "../MachineLearning/LogisticRegression";
import { NeuralNet } from "../MachineLearning/NeuralNet";
import { RidgeRegression } from "../MachineLearning/RidgeRegression";
import { SupportVectorMachine } from "../MachineLearning/SupportVectorMachine";

import * as tf from "@tensorflow/tfjs";
import { matrix } from "mathjs";
import { MultinomialNB } from "ml-naivebayes";
import {
	RandomForestClassifier,
	RandomForestRegression,
} from "ml-random-forest";
import { kNearestNeighbours } from "../MachineLearning/kNearestNeighbours";
import { LinearRegression } from "../MachineLearning/LinearRegression";
import { NaiveBayes } from "../MachineLearning/NaiveBayes";
import { RandomForest } from "../MachineLearning/RandomForest";

export type savedDashboardType = {
	name: string;
	dataset: string;
	fileName: string;
	output: string;
	models: any;
	states: Object;
};

export function convertModels(savedModels: any[]) {
	let convertedModels = [];
	for (let savedModel of savedModels) {
		let converted;
		switch (savedModel.modelName) {
			case ALL_MODELS.linReg:
				converted = convertModelLinearRegression(savedModel);
				break;
			case ALL_MODELS.ridgeReg:
				converted = convertModelLinearRegression(savedModel);
				break;
			case ALL_MODELS.logistic:
				converted = convertModelLogistic(savedModel);
				break;
			case ALL_MODELS.bayes:
				converted = convertModelLinearRegression(savedModel);
				break;
			case ALL_MODELS.knn:
				converted = convertModelLinearRegression(savedModel);
				break;
			case ALL_MODELS.randomForest:
				converted = convertModelRF(savedModel);
				break;
			case ALL_MODELS.svm:
				converted = convertModelSVM(savedModel);
				break;
			case ALL_MODELS.neuralNet:
				converted = convertModelTensor(savedModel);
				break;
		}
		let convertedObj = {};
		convertedObj[savedModel.modelName] = converted;
		convertedModels.push(convertedObj);
	}
	return convertedModels;
}

function convertModelLinearRegression(savedModel: RidgeRegression) {
	let data = JSON.stringify(savedModel);
	return data;
}

function convertModelSVM(savedModel) {
	let svm = new SupportVectorMachine(savedModel.mlDataInfo);
	svm.model = savedModel.model.serializeModel();
	svm.lowestLoss = savedModel.lowestLoss;
	return JSON.stringify(svm);
}

function convertModelLogistic(savedModel) {
	let svm = new LogisticRegression(savedModel.mlDataInfo);
	svm.lowestLoss = savedModel.lowestLoss;
	svm.model = savedModel.model.serializeModel();
	return JSON.stringify(svm);
}

function convertModelTensor(savedModel: NeuralNet) {
	let bestWeights = [];
	let tmp = new NeuralNet(savedModel.mlDataInfo);
	tmp.bestModelWeights = [...savedModel.bestModelWeights];
	tmp.lowestLoss = savedModel.lowestLoss;

	for (let tensor of [...tmp.bestModelWeights]) {
		let tensorArr = tensor.arraySync();
		bestWeights.push(JSON.stringify(tensorArr));
	}
	tmp.bestModelWeights = bestWeights;
	return JSON.stringify(tmp);
}

export function lodModels(saveModelsObj) {
	// Load saved models
	let savedModels = [];
	for (const model of saveModelsObj) {
		const [modelName] = Object.keys(model);
		const [value] = Object.values(model);
		switch (modelName) {
			case ALL_MODELS.linReg:
				savedModels.push(loadLinearRegression(value, LinearRegression));
				break;
			case ALL_MODELS.ridgeReg:
				savedModels.push(loadLinearRegression(value, RidgeRegression));
				break;
			case ALL_MODELS.logistic:
				savedModels.push(loadLogistic(value));
				break;
			case ALL_MODELS.bayes:
				savedModels.push(loadBayes(value));
				break;
			case ALL_MODELS.knn:
				savedModels.push(loadKNN(value));
				break;
			case ALL_MODELS.randomForest:
				savedModels.push(loadRF(value));
				break;
			case ALL_MODELS.svm:
				savedModels.push(loadSVM(value));
				break;
			case ALL_MODELS.neuralNet:
				savedModels.push(loadTensor(value));
		}
	}
	return savedModels;
}

function loadKNN(value: Object) {
	let parsed = JSON.parse(value as string);
	let model = Object.assign(new kNearestNeighbours(undefined), parsed);
	return model;
}
function loadSVM(value: Object) {
	let parsed = JSON.parse(value as string);
	const svm = new SupportVectorMachine(parsed.mlDataInfo);
	svm.model = SVM.load(parsed.model);
	return svm;
}
function loadLogistic(value: Object) {
	let parsed = JSON.parse(value as string);
	const svm = new LogisticRegression(parsed.mlDataInfo);
	svm.model = SVM.load(parsed.model);
	return svm;
}
function loadBayes(value: Object) {
	let parsed = JSON.parse(value as string);
	let model = Object.assign(new NaiveBayes(parsed.mDataInfo), parsed);
	model.model = MultinomialNB.load(model.model);
	return model;
}

function loadRF(value: Object) {
	let parsed = JSON.parse(value as string);

	let rf = new RandomForest(parsed.mlDataInfo);
	let modelType =
		parsed.model.name === "RFRegression"
			? RandomForestRegression
			: RandomForestClassifier;
	rf.model = modelType.load(parsed.model);
	return rf;
}

function loadLinearRegression(value: Object, modelType) {
	let parsed = JSON.parse(value as string);
	let model = Object.assign(new modelType(undefined), parsed);
	let coeff = {
		_data: model.model.coeff.data,
		_size: model.model.coeff.size,
	};
	model.model.coeff = Object.assign(matrix(), coeff);
	return model;
}

function loadTensor(value: Object) {
	let parsed = JSON.parse(value as string);
	let model = new NeuralNet(parsed.mlDataInfo);
	let tensors = parsed.bestModelWeights.map((val) =>
		tf.tensor(JSON.parse(val))
	);
	model.bestModelWeights = tensors;
	model.model.setWeights(tensors);
	return model;
}
